/**
 * Fetch helper for the admin API (cmd/admin, port :8081).
 *
 * Uses Bearer token auth (not session cookies). Token is kept in
 * sessionStorage so it clears on tab close.
 *
 * In dev the Vite proxy rewrites /admin-api/* → http://localhost:8081/*
 * so there are no CORS issues. In prod, reverse-proxy does the same.
 */

export const ADMIN_TOKEN_KEY = "admin_token";

export class AdminApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = "AdminApiError";
    this.status = status;
    this.body = body;
  }
}

export function getAdminToken(): string | null {
  return sessionStorage.getItem(ADMIN_TOKEN_KEY);
}

export function setAdminToken(token: string): void {
  sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
}

export function clearAdminToken(): void {
  sessionStorage.removeItem(ADMIN_TOKEN_KEY);
}

export interface AdminRequestOptions extends Omit<RequestInit, "body"> {
  json?: unknown;
}

/**
 * adminFetch — mirrors apiFetch but uses Bearer token.
 * All paths should start with /admin/... (e.g. /admin/cases).
 * The /admin-api prefix is prepended so the Vite proxy can intercept it.
 */
export async function adminFetch<TResponse = unknown>(
  path: string,
  options: AdminRequestOptions = {},
): Promise<TResponse> {
  const { json, headers, method = "GET", ...rest } = options;

  const finalHeaders = new Headers(headers);
  if (json !== undefined) {
    finalHeaders.set("Content-Type", "application/json");
  }

  const token = getAdminToken();
  if (token) {
    finalHeaders.set("Authorization", `Bearer ${token}`);
  }

  // Proxy prefix: /admin-api/admin/cases → vite rewrites → http://localhost:8081/admin/cases
  const url = `/admin-api${path}`;

  const resp = await fetch(url, {
    ...rest,
    method,
    headers: finalHeaders,
    body: json !== undefined ? JSON.stringify(json) : undefined,
  });

  if (resp.status === 204) return undefined as TResponse;

  const contentType = resp.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const parsed = isJson ? await resp.json().catch(() => null) : await resp.text();

  if (!resp.ok) {
    const message =
      typeof parsed === "string"
        ? parsed
        : (parsed as { error?: string; message?: string })?.error ??
          (parsed as { message?: string })?.message ??
          resp.statusText;
    throw new AdminApiError(resp.status, message, parsed);
  }

  return parsed as TResponse;
}
